<template>
  <div v-if="user">

    <!-- Content -->

    <div ref="card" class="card">
      <div :class="['card-header', currentTab < 3 && currentEvaluation.color ? `card-colored bg-${currentEvaluation.color}-dark` : '']">
        <div>
          <!-- Current snapshot title -->
          <button id="title" class="btn btn-outline-primary dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
            <b>{{ recipientFullName }}</b>
          </button>
          <div class="dropdown-menu snapshot-infos">
            <table class="table-borderless">
              <tbody>
                <tr>
                  <th scope="row">{{ $t('recipient') }}</th>
                  <td>{{ recipientFullName }}</td>
                </tr>
                <tr>
                  <th scope="row">{{ $t('evaluation.snapshot.author') }}</th>
                  <td>{{ snapshotCreator }}</td>
                </tr>
                <tr>
                  <th scope="row">{{ $t('creation') }}</th>
                  <td>{{ currentEvaluation.snapshot ? formatDate(currentEvaluation.snapshot.created_at) : '' }}</td>
                </tr>
                <tr>
                  <th scope="row">{{ $t('evaluation.snapshot.last_updated') }}</th>
                  <td>{{ currentEvaluation.snapshot ? formatDate(currentEvaluation.snapshot.updated_at) : '' }}</td>
                </tr>
                <tr>
                  <th scope="row" class="align-top">{{ $t('evaluation.snapshot.comments') }}</th>
                  <td class="comments">{{ currentEvaluation.snapshot ? currentEvaluation.snapshot.comments : '' }}</td>
                </tr>
                <tr>
                  <th scope="row" class="align-top">{{ $t('evaluation.snapshot.contributors') }}</th>
                  <td>
                    <ul v-if="currentEvaluation.snapshot && currentEvaluation.snapshot.contributors" class="list-unstyled">
                      <li v-for="pro in currentEvaluation.snapshot.contributors">
                        {{ pro.first_name }} {{ pro.last_name }}
                        <span v-if="pro.occupation"> ({{ pro.occupation }}, {{ pro.specialization != null ? pro.specialization : '-' }}/{{ pro.department != null ? pro.department : '-' }})</span>
                      </li>
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Snapshots menu -->
          <div v-if="isSnapshotsMenuVisible" class="btn-group">
            <button type="button" class="btn btn-outline-primary dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
              <fa icon="folder" fixed-width/> {{ currentSnapshot ? currentSnapshot.title : $t('evaluation.snapshots') }}
            </button>
            <div class="dropdown-menu">

              <a v-if="canSaveSnapshot" class="dropdown-item" href="#" @click="saveAs()">{{ $t('save_as') }}</a>
              <a v-if="hasSnapshot && canUpdateSnapshot" class="dropdown-item" href="#" @click="save()">{{ $t('save') }}</a>
              <a v-if="hasSnapshot && canUpdateSnapshot" class="dropdown-item" href="#" @click="$refs.deleteSnapshotModal.show()">{{ $t('delete') }}</a>

              <div v-if="hasSnapshot && currentTab == 1" class="dropdown-divider"/>
              <a v-if="hasSnapshot && currentTab == 1" class="dropdown-item" href="#" @click="showAccompagnyingSheet()">{{ $t('manage.requirements_profiles.display_sheet') }}</a>

              <div v-if="hasSnapshot" class="dropdown-divider"/>
              <a v-if="hasSnapshot" class="dropdown-item" href="#" @click="closeSnapshot()">{{ $t('close') }}</a>

              <div v-if="currentSnapshots.length > 0" class="dropdown-divider"/>
              <a v-for="snapshot in currentSnapshots" :title="createToolTip(snapshot)" class="dropdown-item" href="#" @click="applySnapshot(snapshot)">{{ snapshot.title }}</a>

              <div v-if="isRequirementsTab" class="dropdown-divider"/>
              <a v-if="isRequirementsTab" class="dropdown-item" href="#" @click="openSelector()">{{ $t('manage.requirements_profiles.open_publics') }}</a>

            </div>
          </div>

          <!-- Masks menu -->
          <div v-if="isMasksMenuVisible" class="btn-group">
            <button type="button" class="btn btn-outline-primary dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
              <fa icon="ban" fixed-width/> {{ currentMaskTitle }}
            </button>
            <div class="dropdown-menu">

              <a v-if="canSaveSnapshot" class="dropdown-item" href="#" @click="newMask()">{{ $t('new') }}</a>
              <a v-if="currentMask" class="dropdown-item" href="#" @click="saveMaskAs()">{{ $t('save_as') }}</a>
              <a v-if="isAuthorOfMask && !isMaskApproved" class="dropdown-item" href="#" @click="editMask()">{{ $t('edit') }}</a>
              <a v-if="isAuthorOfMask && !isMaskApproved" class="dropdown-item" href="#" @click="$refs.deleteMaskModal.show()">{{ $t('delete') }}</a>

              <div v-if="currentMask" class="dropdown-divider"/>
              <a v-if="currentMask" class="dropdown-item" href="#" @click="closeMask()">{{ $t('close') }}</a>

              <div v-if="masks.length > 0"class="dropdown-divider"/>
              <template v-if="masks.length < 10">
                <a v-for="mask in masks" class="dropdown-item" href="#" @click="applyMask(mask)">{{ mask.title }}</a>
              </template>
              <template v-else>
                <a class="dropdown-item" href="#" @click="$refs.masksSelector.show()">{{ $t('evaluation.mask.browse') }}</a>
              </template>
              <!--a v-if="masks.length == 0" class="dropdown-item muted">{{ $t('evaluation.mask.no_mask') }}</a-->

            </div>
          </div>
        </div>

        <!-- Tabs -->
        <ul id="pills-tab" class="nav nav-tabs card-header-tabs" role="tablist">
          <li v-for="(evaluation, key) in evaluations" v-if="!isRecipient || !evaluation.hideIfRecipient" class="nav-item">
            <span :aria-controls="evaluation.title"
                  :href="`#${evaluation.title}`"
                  :id="`${evaluation.title}-tab`"
                  :class="{ 'nav-link': true, 'active': currentTab == key }"
                  aria-selected="true"
                  data-toggle="pill"
                  role="tab"
                  @click="setTab(key)">
              <fa :icon="evaluationsTypes[key].icon" /> {{ $t(evaluationsTypes[key].title) }}{{ evaluation.snapshot ? ` (${evaluation.snapshot.title})` : '' }} {{ evaluation.updated ? ' *' : '' }}
            </span>
          </li>
          <li class="nav-item">
            <span id="reports-tab"
                  :class="{ 'nav-link': true, 'active': currentTab == 3 }"
                  aria-controls="reports"
                  aria-selected="true"
                  data-toggle="pill"
                  href="#reports"
                  role="tab"
                  @click="setTab(3)">
              <fa icon="print" /> {{ $t('reports.reports') }}
            </span>
          </li>
          <li class="nav-item">
            <span id="decisions-tab"
                  :class="{ 'nav-link': true, 'active': currentTab == 4 }"
                  aria-controls="decisions"
                  aria-selected="true"
                  data-toggle="pill"
                  href="#decisions"
                  role="tab"
                  @click="setTab(4)">
              <fa icon="check" /> {{ $t('decisions.title') }}
            </span>
          </li>
        </ul>
      </div>

      <!-- Body -->
      <div id="pills-tabContent" class="card-body tab-content">
        <div v-for="(evaluation, key) in evaluations"
             :class="{ 'active': currentTab == key }"
             :id="evaluation.title"
             :aria-labelledby="`${evaluation.title}-tab`"
             class="tab-pane fade show"
             role="tabpanel">
          <Evaluation ref="evaluation"
                      :evaluation="evaluation"
                      @info="$refs.infoModal.show()"
                      @updated="onUpdate(evaluation, key)" />
        </div>
        <div id="reports"
             :class="{ 'active': currentTab == 3 }"
             class="tab-pane"
             role="tabpanel"
             aria-labelledby="reports-tab">
          <Reports v-if="recipient" :recipient="recipient"
                   :ability-snapshot="evaluations[0].snapshot" :requirement-snapshot="evaluations[1].snapshot"
                   :perception-snapshot="evaluations[2].snapshot" :masks="masks" :apt-mask="currentMask ? currentMask.id : 0" />
        </div>
        <div id="decisions"
             :class="{ 'active': currentTab == 4 }"
             class="tab-pane"
             role="tabpanel"
             aria-labelledby="reports-tab">
          <Decisions :recipient="recipient" />
        </div>
      </div>
    </div>

    <!-- Modals -->

    <b-modal ref="deleteMaskModal"
             :title="$t('evaluation.mask.delete')"
             :ok-title="$t('delete')"
             :cancel-title="$t('cancel')"
             ok-variant="danger"
             centered
             @ok.prevent="deleteMask()">
      <form @submit.prevent="deleteMask()" @keydown="form.onKeydown($event)">
        <div class="form-group">
          {{ $t('evaluation.ask_remove', [ currentMaskTitle ]) }}
        </div>
      </form>
    </b-modal>

    <MaskModal ref="maskModalEval" @saved="saveMask"/>

    <b-modal ref="deleteSnapshotModal"
             :title="$t('evaluation.snapshot.delete')"
             :ok-title="$t('delete')"
             :cancel-title="$t('cancel')"
             ok-variant="danger"
             centered
             @ok.prevent="deleteSnapshot()">
      <form @submit.prevent="deleteSnapshot()" @keydown="form.onKeydown($event)">
        <div class="form-group">
          {{ $t('evaluation.ask_remove', [ currentEvaluation.snapshot ? currentEvaluation.snapshot.title : '' ]) }}
        </div>
      </form>
    </b-modal>

    <b-modal ref="saveSnapshotModal"
             :title="$t('evaluation.snapshot.save')"
             :ok-title="$t('save')"
             :cancel-title="$t('cancel')"
             centered
             @ok.prevent="saveSnapshot()">
      <form @submit.prevent="saveSnapshot()" @keydown="form.onKeydown($event)">
        <div class="form-group">
          <label for="snapshot-title" class="col-form-label">{{ $t('evaluation.snapshot.name') }} :</label>
          <input v-model="form.title" name="snapshot-title" type="text" class="form-control" maxlength="100">
          <form-error :form="form" field="title" />
        </div>
        <div class="form-group">
          <label for="snapshot-comments" class="col-form-label">{{ $t('evaluation.snapshot.comments') }} :</label>
          <textarea v-model="form.comments" name="snapshot-comments" class="form-control" rows="8"/>
          <form-error :form="form" field="comments" />
        </div>
        <div v-if="potentialContributors.length > 0" class="form-group">
          <div class="col-form-label">
            {{ $t('evaluation.snapshot.contributors') }} :
          </div>
          <div v-for="pro in potentialContributors">
            <div class="custom-control custom-checkbox">
              <input :value="pro.id" :id="'contributor'+pro.id" v-model="form.contributors" type="checkbox" class="custom-control-input">
              <label :for="'contributor'+pro.id" class="custom-control-label">{{ pro.first_name }} {{ pro.last_name }}</label>
            </div>
          </div>
        </div>
      </form>
      <div v-if="isRequirementsTab && (currentSnapshot && currentSnapshot.accompanying_sheet != null && !savingNew)" slot="modal-footer" class="w-100">
        <Checkbox v-model="showSheetForm" name="cbxSheet" class="float-left with-buttons-on-right">{{ $t('manage.requirements_profiles.display_sheet') }}</Checkbox>
        <div class="float-right">
          <b-btn variant="secondary" @click="$refs.saveSnapshotModal.hide()">{{ $t('cancel') }}</b-btn>
          <b-btn variant="primary" @click="saveSnapshot()">{{ $t('save') }}</b-btn>
        </div>
      </div>
    </b-modal>

    <!-- Evaluations info tables -->
    <b-modal ref="infoModal" size="lg" ok-only hide-header>
      <SkillsTable v-if="currentTab == 0" />
      <RequirementsTable v-if="currentTab == 1" />
      <PerceptionsTable v-if="currentTab == 2" />
    </b-modal>

    <AccompanyingSheet v-if="isRequirementsTab" ref="accompanyingSheet" :read-only="!canUpdateSnapshot" :snapshot="currentSnapshot" @saved="updateSnapSheet"/>
    <ProfilesSelector ref="selector" :profiles="requirementsProfiles" @openSnaphot="applySnapshot" />
    <MasksSelector ref="masksSelector" :masks="masks" @openMask="applyMask" />

  </div>
</template>

<script>
import Form from 'vform'
import axios from 'axios'
import Vue from 'vue'
import { mapGetters } from 'vuex'
import { isProfessional, isRecipient, isManager, isOrganizationLeader } from '~/plugins/auth'

export default {
  name: 'Evaluations',

  props: {
    recipient: { required: true, type: Object }
  },

  computed: {
    canSaveSnapshot () {
      return this.user && ((!isRecipient(this.user) && !isManager(this.user)) || (isRecipient(this.user) && this.currentTab == 2 && this.permissions.canEditPerceptionReport))
    },

    canUpdateSnapshot () {
      if (!this.hasSnapshot) {
        return false
      }

      if (!this.isRequirementsTab) {
        return this.user && ((this.currentSnapshot.creator_id == this.user.id) || (!isRecipient(this.user) && !isManager(this.user)))
      } else {
        return this.user && (!isRecipient(this.user) && !isManager(this.user)) &&
          (!this.currentSnapshot.accompanying_sheet ||
          (this.currentSnapshot.accompanying_sheet && (this.currentSnapshot.creator_id == this.user.id || isOrganizationLeader(this.user)) && !this.currentSnapshot.accompanying_sheet.approved))
      }
    },

    currentEvaluation () {
      return this.evaluations[this.currentTab % this.evaluations.length]
    },

    currentMaskTitle () {
      return this.currentMask ? this.currentMask.title : this.$t('evaluation.masks')
    },

    currentSnapshot () {
      return this.currentEvaluation.snapshot
    },

    currentSnapshots () {
      const evaluationId = this.currentEvaluation.id
      let filtered = this.snapshots.filter(snapshot => snapshot.evaluation_id == evaluationId)

      if (this.isRequirementsTab) {
        filtered = filtered.filter(x => !x.accompanying_sheet || (x.accompanying_sheet && !x.accompanying_sheet.approved))
      }

      return filtered
    },

    hasSnapshot () {
      if (this.currentSnapshot) {
        return this.currentSnapshot.hasOwnProperty('id')
      }

      return false
    },

    isAuthorOfMask () {
      return this.user && this.currentMask && this.currentMask.creator_id == this.user.id
    },

    isMaskApproved () {
      return this.currentMask.approved
    },

    isMasksMenuVisible () {
      return this.currentTab == 0
    },

    isProfessional () {
      return isProfessional(this.user)
    },

    isRecipient () {
      return isRecipient(this.user)
    },

    isRequirementsTab () {
      return this.currentTab == 1
    },

    isSnapshotsMenuVisible () {
      return this.currentTab < 3
    },

    potentialContributors () {
      var pros = this.recipient.recipient.professionals // Yes, 'recipient.recipient' is correct.
      return pros.filter(p => p.id != this.user.id, this)
    },

    recipientFullName () {
      return this.recipient.first_name + ' ' + this.recipient.last_name
    },

    snapshotCreator () {
      return this.getSnapshotCreator()
    },

    ...mapGetters({
      evaluationsTypes: 'evaluations/evaluations',
      user: 'auth/user',
      permissions: 'auth/permissions'
    })
  },

  data: () => ({
    evaluations: [
      {
        id: 1,
        title: 'abilities',
        color: 'red',
        data: [],
        defaultValue: 5,
        snapshot: null,
        updated: false,
        hideIfRecipient: true
      }, {
        id: 2,
        title: 'requirements',
        color: 'purple',
        data: [],
        defaultValue: 0,
        snapshot: null,
        updated: false,
        hideIfRecipient: true
      }, {
        id: 3,
        title: 'perceptions',
        color: 'turquoise',
        data: [],
        defaultValue: 5,
        snapshot: null,
        updated: false,
        hideIfRecipient: false
      }
    ],
    currentTab: 0,
    currentMask: null,
    masks: [],
    snapshots: [],
    requirementsProfiles: [],
    form: new Form({
      id: 0,
      evaluation_id: 0,
      recipient_id: 0,
      title: new Date().toJSON().slice(0, 10).split('-').reverse().join('/'),
      comments: '',
      contributors: []
    }),
    snapshotTitle: '',
    showSheetForm: false,
    savingNew: false
  }),

  mounted () {
    // Initialize evaluations.
    this.evaluationsTypes.forEach((type, index) => {
      this.evaluations[index].id = type.id
      this.evaluations[index].title = type.title
      this.evaluations[index].defaultValue = type.default_value
      this.evaluations[index].icon = type.icon
      this.evaluations[index].data = this.$store.getters['evaluations/' + type.title].tree
    })

    this.resetTrees()
    this.loadMasks()
    this.loadSnapshots()
    this.loadRequirementsProfiles()

    if (this.isRecipient) { this.currentTab = 2 }
  },

  methods: {
    /**
     * Recursive subroutine to apply mask
     *
     * @param mask List of nodes to show and ignore.
     */
    $_applyMask (mask, index, data) {
      index.value++

      for (let i = 0; i < data.length; i++) {
        let node = data[i]
        let masked = mask.mask[index.value] == '0'

        Vue.set(node, 'masked', masked)

        if (masked) {
          index.value++
          continue
        } else {
          this.$_applyMask(mask, index, node.children)
        }
      }
    },

    /**
     * Apply given mask to tree views.
     *
     * @param mask List of nodes to show and ignore.
     */
    applyMask (mask) {
      const data = this.evaluations[0].data // evaluations[0] means abilities
      const index = { value: 0 } // Iterator.

      // Recursive application.
      this.$_applyMask(mask, index, data[0].children)

      this.currentMask = mask
    },

    /**
     * Subroutine for applySnapshot.
     *
     * @param data Snapshot list.
     * @param index Index in the snapshot list.
     * @param node Node in data tree.
     * @param defaultValue Value to ignore (don't save default value).
     */
    $_applySnapshot (data, index, node, defaultValue) {
      // Only save value of leafs.
      // If parent, go find the next leaf.
      if (node.children.length == 0) {
        let datas = data[index[0]]
        let value = defaultValue
        let comment = ''

        // If datas contain only one item
        //    -> if Number : value
        //    -> if String : comment
        // Else, contain value and comment.
        if (datas.length == 1) {
          if (Number.isInteger(datas[0])) {
            value = datas[0]
          } else {
            comment = datas[0]
          }
        } else if (datas.length == 2) {
          value = datas[0]
          comment = datas[1]
        }

        // Assign properties to node.
        Vue.set(node, 'value', value)
        Vue.set(node, 'comment', comment)

        // Use next item in snapshot list.
        index[0] += 1
      } else {
        for (let i = 0; i < node.children.length; i++) {
          this.$_applySnapshot(data, index, node.children[i], defaultValue)
        }
      }
    },

    /**
     * Apply snapshot to tree views.
     *
     * @param snapshot List of values to assign.
     */
    applySnapshot (snapshot) {
      // If snapshot has an associated mask, apply it!
      if (snapshot.evaluation_mask_id) {
        var mask = this.masks.find(m => m.id === snapshot.evaluation_mask_id)
        this.applyMask(mask)
      } else {
        this.closeMask()
      }

      let data = JSON.parse(snapshot.snapshot)
      let evaluation = this.currentEvaluation.data
      let index = [ 0 ] // Value in array so it can be passed by reference.
      this.$_applySnapshot(data, index, evaluation[0], this.currentEvaluation.defaultValue)

      // Vue.set(this.currentEvaluation, 'snapshot', snapshot);
      this.currentEvaluation.snapshot = snapshot
      this.currentEvaluation.updated = false

      this.updateCurrentEvaluation()
    },

    closeMask () {
      this.currentMask = null
      this.resetMask(this.currentEvaluation.data[0])
      this.updateCurrentEvaluation()
    },

    closeSnapshot () {
      this.currentEvaluation.updated = false
      this.currentEvaluation.snapshot = null

      this.resetSnapshot(this.currentEvaluation.data[0])
      this.updateCurrentEvaluation()
    },

    createToolTip (snapshot) {
      var lines = [this.$t('evaluation.snapshot.author') + ': ' + this.snapshotCreator,
        this.$t('creation') + ': ' + this.formatDate(snapshot.created_at),
        this.$t('evaluation.snapshot.last_updated') + ': ' + this.formatDate(snapshot.updated_at),
        this.$t('evaluation.snapshot.comments') + ': ' + (snapshot.comments == null ? '' : snapshot.comments),
        this.$t('evaluation.snapshot.contributors') + ': ' + snapshot.contributors.map(c => c.first_name + ' ' + c.last_name).join(', ')
      ]

      return lines.join('\n')
    },

    async deleteMask () {
      await axios.delete('/api/evaluations/masks/' + this.currentMask.id)
        .then((response) => {
        // Remove snapshot from list.
          let index = this.masks.indexOf(this.currentMask)
          this.masks.splice(index, 1)

          // Reset data.
          this.closeMask()

          // Hide modal.
          this.$refs.deleteMaskModal.hide()
        })
        .catch((error) => {
          console.error(error)
        })
    },

    async deleteSnapshot () {
      await axios.delete('/api/users/' + this.recipient.id + '/evaluations/snapshots/' + this.currentEvaluation.snapshot.id)
        .then((response) => {
        // Remove snapshot from list.
          let index = this.snapshots.indexOf(this.currentEvaluation.snapshot)
          this.snapshots.splice(index, 1)

          // Reset data.
          this.closeSnapshot()

          // Hide modal.
          this.$refs.deleteSnapshotModal.hide()
        })
        .catch((error) => {
          console.error(error)
        })
    },

    editMask () {
      if (this.currentMask) { this.$refs.maskModalEval.editMask(this.currentMask) }
    },

    formatDate (date) {
      if (date) {
        return Date.fromDateTime(date).toUserLocaleString(this)
      }

      return ''
    },

    getSnapshotCreator () {
      if (this.currentSnapshot && this.currentSnapshot.hasOwnProperty('created_by')) {
        const author = this.currentEvaluation.snapshot.created_by
        let name = author.first_name + ' ' + author.last_name

        if (author.occupation) {
          name += ` (${author.occupation})`
        }

        return name
      }

      return ''
    },

    async loadMasks () {
      await axios.get('/api/evaluations/masks')
        .then((response) => {
          this.masks = response.data
        })
        .catch((error) => {
          console.error(error)
          this.form = error.data
        })
    },

    async loadRequirementsProfiles () {
      await axios.get('/api/evaluations/requirements')
        .then((response) => {
          this.requirementsProfiles = response.data
        })
        .catch((error) => {
          console.error(error)
          this.form = error.data
        })
    },

    async loadSnapshots () {
      await axios.get('/api/users/' + this.recipient.id + '/evaluations/snapshots')
        .then((response) => {
          this.snapshots = response.data
        })
        .catch((error) => {
          console.error(error)
          this.form = error.data
        })
    },

    newMask () {
      this.$refs.maskModalEval.new()
    },

    onUpdate (evaluation, key) {
      evaluation.updated = true
    },

    openSelector () {
      this.$refs.selector.show()
    },

    resetMask (data) {
      Vue.delete(data, 'masked')

      for (let i = 0; i < data.children.length; i++) {
        this.resetMask(data.children[i])
      }
    },

    resetSnapshot (data) {
      if (data.children.length > 0) {
        for (let i = 0; i < data.children.length; i++) {
          this.resetSnapshot(data.children[i])
        }
      } else {
        Vue.delete(data, 'value')
        Vue.set(data, 'comment', '')
      }
    },

    resetForm () {
      this.form.id = 0
      this.form.recipient_id = 0
      this.form.evaluation_mask_id = null
      this.form.title = new Date(Date.now()).toUserLocaleString(this)
      this.form.comments = ''
      this.form.contributors = []
    },

    resetTrees () {
      for (var i = 0; i < this.evaluations.length; i++) {
        this.evaluations[i].snapshot = null
        this.resetSnapshot(this.evaluations[i].data[0])
        this.$refs['evaluation'][i].update()
      }
    },

    save () {
      this.savingNew = false

      var snapshot = this.currentEvaluation.snapshot
      this.form.id = snapshot.id
      this.form.recipient_id = snapshot.recipient_id
      this.form.title = snapshot.title
      this.form.comments = snapshot.comments
      this.form.contributors = snapshot.contributors.map(c => c.id)

      if (this.hasSnapshot && this.canUpdateSnapshot) {
        this.saveSnapshot()
      }
    },

    saveAs () {
      this.resetForm()
      this.savingNew = true
      this.$refs.saveSnapshotModal.show()
    },

    saveMask (mask) {
      if (!this.$refs.maskModalEval.edit) {
        this.masks.push(mask)
        this.currentMask = mask
      } else {
        var index = this.masks.indexOf(this.masks.filter(x => x.id == mask.id)[0])
        Vue.set(this.masks, index, mask)
      }
      this.applyMask(mask)
    },

    saveMaskAs () {
      if (this.currentMask) { this.$refs.maskModalEval.editMask(this.currentMask, false) }
    },

    async saveSnapshot () {
      let evaluationId = this.evaluationsTypes[this.currentTab].id

      // Populate form.
      let snapshot = this.$refs.evaluation[this.currentTab].getSnapshot()

      this.form.evaluation_id = evaluationId
      this.form.recipient_id = this.recipient.id
      this.form.snapshot = JSON.stringify(snapshot)

      // Send form.
      try {
        const isNew = this.form.id == 0
        const url = `/api/users/${this.recipient.id}/evaluations/snapshots`

        if (isNew) {
          // For aptitudes, use the current selected mask id, add only in form object
          if (evaluationId == 1) { this.form.evaluation_mask_id = (this.currentMask) ? this.currentMask.id : null }

          const response = await this.form.post(url)
          const data = response.data

          // Push new snapshot into datas.
          this.snapshots.push(data)
          this.currentEvaluation.snapshot = data
          this.applySnapshot(data)
        } else {
          // For aptitudes, use the current selected mask id, add in form object and currentsnapshot
          if (evaluationId == 1) {
            this.form.evaluation_mask_id = (this.currentMask) ? this.currentMask.id : null
            this.currentSnapshot.evaluation_mask_id = (this.currentMask) ? this.currentMask.id : null
          }

          const response = await this.form.patch(url)
          const data = response.data
          const index = this.snapshots.map(x => x.id).indexOf(data.id)

          this.updateContributors(data.contributors)

          Vue.set(this.snapshots, index, data)
        }

        this.currentSnapshot.title = this.form.title
        this.currentEvaluation.updated = false

        // Hide and reset modal.
        this.$refs.saveSnapshotModal.hide()

        // Show accompagnying sheet if necessary.
        if (this.isRequirementsTab && (this.showSheetForm || this.savingNew)) {
          this.showAccompagnyingSheet()
        }
      } catch (error) {
        console.error(error)
      }
    },

    setTab (index) {
      this.currentTab = index
    },

    showAccompagnyingSheet () {
      this.$refs.accompanyingSheet.show()
    },

    /**
     * Updates contributors in form.
     *
     * @param contributors Contributors to add in form.
     */
    updateContributors (contributors) {
      const potentialContributors = this.potentialContributors.map(m => m.id)
      this.form.contributors = contributors.filter(c => this.potentialContributors.includes(c.id)).map(c => c.id)
    },

    updateCurrentEvaluation () {
      this.$refs['evaluation'][this.currentTab].update()
      if (this.currentSnapshot) {
        this.showSheetForm = this.isRequirementsTab && this.currentSnapshot.accompanying_sheet == null
      }
    },

    updateSnapSheet ({ sheet, authors }) {
      if (this.currentSnapshot) {
        this.currentSnapshot.accompanying_sheet = sheet

        this.updateContributors(authors)
      }
    }
  }
}
</script>

<style scoped>
.card-header div:first-child {
  margin: 0.5rem 0 1rem 0;
}

.card-header .card-header-tabs {
  margin-left: 0rem;
}

.card-header-tabs .nav-link .svg-inline--fa {
  font-size: unset;
}

.card-header .btn-outline-primary:not(#title) {
  border: 1px solid transparent;
}

.card-header .btn-outline-secondary:hover {
  border: 1px solid $secondary;
}

.snapshot-infos {
  padding: 1em;
}

.snapshot-infos th,
.snapshot-infos td {
  padding: 0 0.5em;
}

.snapshot-infos th {
  text-align: right;
}

span.nav-link {
  cursor: pointer;
  user-select: none;
}

.comments {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
}

#acc-sheet-modal {
  overflow-y: auto;
}
</style>



// WEBPACK FOOTER //
// ../../../../resources/js/components/Evaluations.vue